/* eslint-disable no-unused-vars */
import clsx from "clsx";
import { Button } from "@/components/UI/Button";
import { getMainBlueActionButtonSize } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import useTableUIStore from "@/store/tableUIStore";
import { TicketInstrument } from "@/store/tradeFromScratchStore";
import { returnSendButtonLabel } from "../helper/ticketSharedHelper";
import InstrumentSelect from "./InstrumentSelect";

// LQC-2493 - actions left, instrument right. Shared by every ticket so switching
// instrument never moves the controls, and so the send/clear behaviour is written once.
// testIdPrefix keeps each ticket ids its own ("BondTicketSendButton").

interface ITicketHeader {
  testIdPrefix: string;
  instrument: TicketInstrument;
  onInstrumentChange: (instrument: TicketInstrument) => void;
  canSend: boolean;
  isSending: boolean;
  isConfirming: boolean;
  onSend: () => void;
  onClear: () => void;
  // Fires while the user hovers a blocked Send - the ticket lights up the rows in its way.
  onSendHoverChange: (isHovering: boolean) => void;
}

const TicketHeader = ({
  testIdPrefix,
  instrument,
  onInstrumentChange,
  canSend,
  isSending,
  isConfirming,
  onSend,
  onClear,
  onSendHoverChange,
}: ITicketHeader) => {
  // Same sizing as Trade Creation's buttons so both headers track the font size setting.
  const fontSize = useTableUIStore((state) => state.fontSize);
  const buttonSize = getMainBlueActionButtonSize(fontSize);

  return (
    // Wraps rather than overflowing at the widget minimum width (minW 3).
    <div
      data-testid={`${testIdPrefix}Header`}
      id={`${testIdPrefix}Header`}
      className="flex flex-wrap items-center gap-2 border-b border-gray-300 p-1 pb-2 dark:border-gray-600"
    >
      {/* A disabled button fires no mouse events, so the hover lives on the wrapper. */}
      <span
        data-testid={`${testIdPrefix}SendButtonWrapper`}
        id={`${testIdPrefix}SendButtonWrapper`}
        onMouseEnter={() => onSendHoverChange(true)}
        onMouseLeave={() => onSendHoverChange(false)}
      >
        <Button
          data-testid={`${testIdPrefix}SendButton`}
          id={`${testIdPrefix}SendButton`}
          variant="mainBlueAction"
          size={buttonSize}
          disabled={!canSend}
          // Fixed width so the shorter confirmation label does not shuffle Clear along.
          className={clsx(
            "min-w-[11rem]",
            isConfirming &&
              "bg-green-600 text-white hover:bg-green-600 dark:bg-green-600 dark:text-white dark:hover:bg-green-600",
            !canSend && "cursor-not-allowed",
            !canSend && !isConfirming && "opacity-50",
          )}
          onClick={onSend}
        >
          {returnSendButtonLabel(isSending, isConfirming)}
        </Button>
      </span>
      <Button
        data-testid={`${testIdPrefix}ClearButton`}
        id={`${testIdPrefix}ClearButton`}
        variant="secondaryBlueActionAtTradeCreation"
        size={buttonSize}
        disabled={isSending}
        onClick={onClear}
      >
        Clear
      </Button>
      <div className="ml-auto">
        <InstrumentSelect
          testId={`${testIdPrefix}InstrumentInput`}
          value={instrument}
          onChange={onInstrumentChange}
          isDisabled={isSending}
        />
      </div>
    </div>
  );
};

export default TicketHeader;
